import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import footprint from "../assets/footprint-fill.png";
import { IoFootsteps } from "react-icons/io5";
import { RiPinDistanceFill, RiFireLine } from "react-icons/ri";



interface ApexChartProps {
  type: "bar" | "line";
  series: Array<{ name: string; data: number[] }>;
  categories: string[];
  selectedMetric: string;
  onMetricChange: (metric: string) => void;
  yAxisMax?: number;
  units: {
    steps: string;
    distance: string;
    caloriesburned: string;
  };
  summary?: {
    steps?: { avg: number; min: number; max: number };
    distance?: { avg: number; min: number; max: number };
    caloriesburned?: { avg: number; min: number; max: number };
  };
}
type MetricKey = "steps" | "distance" | "caloriesburned";
const Graph: React.FC<ApexChartProps> = ({ type, series, categories, onMetricChange, yAxisMax, selectedMetric, units, summary, }) => {
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setChartKey((prev) => prev + 1);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style", "data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const textColor = getComputedStyle(document.documentElement).getPropertyValue("--text-color").trim();
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue("--svg-color").trim(); // Replaces #094752
  const secondaryColor = getComputedStyle(document.documentElement).getPropertyValue("--legend-color").trim();
  // const [currentMetric, setCurrentMetric] = useState(selectedMetric);
  // const [chartSeries, setChartSeries] = useState(series);

  // useEffect(() => {
  //   setCurrentMetric(selectedMetric);
  //   setChartSeries(series);
  // }, [selectedMetric, series]);
  // console.log("Selected Metric in Graph123:", selectedMetric);
  const handleMetricChange = (metric: string) => {
    // setCurrentMetric(metric);
    onMetricChange(metric);
  };

  const achievedData = series[1]?.data || [];
  const targetData = series[0]?.data || [];

  const remainingTarget = targetData.map((target, i) =>
    target > achievedData[i] ? target - achievedData[i] : 0
  );

  const adjustedSeries = [
    { name: "Achieved", data: achievedData },
    { name: "Target", data: remainingTarget }
  ];

  // const metricUnits: Record<string, string> = {
  //   steps: "Steps (count)",
  //   distance: "Distance (Km)",
  //   caloriesburned: "Calories (Kcal)",
  // };

  const getUnitLabel = (metric: string): string => {
    const key = metric.toLowerCase() as MetricKey;
    const displayName = {
      steps: "Steps",
      distance: "Distance",
      caloriesburned: "Calories",
    }[key];

    return `${displayName} (${units[key] || ""})`;
  };
  const getSummaryText = (metric: string): string => {
    const key = metric.toLowerCase() as MetricKey;
    const s = summary?.[key];
    if (!s) return "";
    // format as you like
    return `Avg: ${s.avg} • Min: ${s.min} • Max: ${s.max}`;


  };

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: type,
      height: 250,
      stacked: true,
      foreColor: textColor, // Applies text color to all axes/labels
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 5,
        columnWidth: "30%",
      },
    },
    xaxis: {
      type: "category",
      categories: categories,
      labels: {
        style: { colors: textColor }, // Ensure x-axis labels match
      },
    },
    yaxis: {
      tickAmount: yAxisMax && yAxisMax <= 5 ? yAxisMax : undefined,
      min: 0,
      max: yAxisMax,
      labels: {
        style: { colors: textColor }, // Ensure y-axis labels match
        formatter: (val: number) => {
          return Number.isInteger(val) ? val.toFixed(0) : Math.round(val).toFixed(0);
        },
      },
    },
    fill: {
      opacity: 1,
      colors: [primaryColor, secondaryColor], // Dynamic Colors
    },
    colors: [primaryColor, secondaryColor], // Dynamic Colors
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      theme: "dark", // Keep tooltip legible
      shared: true,
      intersect: false,
      y: {
        formatter: (value, { seriesIndex, dataPointIndex }) => {
          if (seriesIndex === 0) {
            return `${series[1].data[dataPointIndex]}`;
          } else {
            return `${series[0].data[dataPointIndex]}`;
          }
        },
      },
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          legend: { position: "bottom", offsetX: -10, offsetY: 0 },
        },
      },
    ],
  };

  return (
    <div className="graph-container">
      <div className="MetricToggleContainer">
        {/* <div className="MetricLabel">{metricUnits[selectedMetric.toLowerCase()]}</div> */}
        <div className="MetricLabel">{getUnitLabel(selectedMetric)}
        </div>

        <div className="Metric-Icons">
          <button
            className={`GraphIcon ${selectedMetric.toLowerCase() === "steps" ? "active" : ""}`}
            onClick={() => handleMetricChange("steps")}
          >
            <IoFootsteps style={{ height: "1.4rem", width: "1rem" }} />
          </button>
          <button
            className={`GraphIcon ${selectedMetric === "distance" ? "active" : ""}`}
            onClick={() => handleMetricChange("distance")}
          >
            <RiPinDistanceFill style={{ height: "1.4rem", width: "1rem" }} />
          </button>
          <button
            className={`GraphIcon ${selectedMetric === "caloriesburned" ? "active" : ""}`}
            onClick={() => handleMetricChange("caloriesburned")}
          >
            <RiFireLine style={{ height: "1.4rem", width: "1rem" }} />
          </button>
        </div>
      </div>
      <ReactApexChart key={chartKey} options={options} series={adjustedSeries} type={type} height={200} />
    </div>
  );
};

export default Graph;